/**
 * Lenis smooth scroll — desktop only (squeeze layout, >=990px), where `.page-wrapper` is the
 * real scroll container (see assets/scroll-container.js). Mobile keeps native document scroll
 * untouched (already has good momentum/inertia and lets the address bar hide/show).
 *
 * History: tried twice before (2026-09-11, 2026-09-14) and reverted both times after automated
 * Chrome testing showed the preview tab become completely unresponsive. Root cause found
 * 2026-09-14 (third look): the test tab had lost visibility/focus mid-test (other tabs were
 * being driven in the same browser window). Chrome throttles/pauses `requestAnimationFrame` in
 * hidden background tabs — and Lenis's `autoRaf` depends entirely on a continuous rAF loop — so
 * a backgrounded test tab looks exactly like "the page froze" even though nothing is actually
 * wrong. This matches what real usage showed (smooth scroll felt fine on the home page). Third
 * attempt: same lean approach (no GSAP/ScrollTrigger), tested in a single tab kept focused and
 * visible throughout.
 *
 * Respects prefers-reduced-motion: Lenis is not created at all when the user has that on.
 */
(function () {
  if (window.__upLenisInit) return;
  window.__upLenisInit = true;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (typeof window.Lenis !== 'function') return;

  var DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');
  var PAGE_WRAPPER_SELECTOR = '.page-wrapper';

  /** @type {import('lenis').default | null} */
  var lenis = null;

  /** @type {MutationObserver | null} */
  var contentGrowthObserver = null;
  var resizeDebounceTimer = null;

  function destroyLenis() {
    if (contentGrowthObserver) {
      contentGrowthObserver.disconnect();
      contentGrowthObserver = null;
    }
    clearTimeout(resizeDebounceTimer);
    if (!lenis) return;
    lenis.destroy();
    lenis = null;
    window.upLenis = null;
  }

  function createLenis() {
    if (lenis) return;
    var wrapper = document.querySelector(PAGE_WRAPPER_SELECTOR);
    if (!wrapper) return;

    lenis = new window.Lenis({
      wrapper: wrapper,
      content: wrapper,
      autoRaf: true,
      // Keep the feel close to native: subtle smoothing, not a heavy "floaty" effect.
      lerp: 0.12,
      wheelMultiplier: 1,
      touchMultiplier: 1,
      // syncTouch left off on purpose: this is desktop-only (>=990px), no touch input expected.
      // Dialogs with their own scroll (cart items, search results, popups) scroll
      // natively: without this, the wheel over them moves the page behind.
      prevent: (node) => !!node.closest?.('dialog'),
    });

    window.upLenis = lenis;

    // Lenis measures `.page-wrapper`'s scrollHeight once on init and caches it as the scroll
    // limit. It only re-measures via ResizeObserver on the wrapper/content elements it was given
    // — but `.page-wrapper` has a FIXED height (100dvh, overflow-y: auto), so its own box never
    // resizes when children grow; only its *scrollHeight* does, which ResizeObserver does not
    // watch. Collection pages append more product rows after Lenis's first measurement (infinite
    // scroll / "show more"), so the cached limit goes stale at whatever height was loaded at init
    // — observed as: wheel scroll stops partway down, dragging the native scrollbar still works
    // (bypasses Lenis), then the next wheel tick snaps back to the stale limit. Watching the
    // wrapper's subtree for DOM growth and re-measuring fixes it for any page whose content grows
    // after load, not just collection pages.
    function scheduleResize() {
      clearTimeout(resizeDebounceTimer);
      resizeDebounceTimer = setTimeout(function () {
        if (lenis) lenis.resize();
      }, 150);
    }

    contentGrowthObserver = new MutationObserver(scheduleResize);
    // `attributes: true` se agrega porque el selector de columnas (4 → más) probablemente
    // solo cambia una clase/atributo en la grilla existente, sin agregar ni quitar elementos
    // — con solo childList ese caso no disparaba el resize.
    contentGrowthObserver.observe(wrapper, { childList: true, subtree: true, attributes: true });

    // Las imágenes de producto se cargan de forma perezosa: cuando terminan de cargar DESPUÉS
    // del primer cálculo de Lenis, la página crece en altura sin que el MutationObserver lo vea
    // (no se agregan ni quitan elementos, solo cambia el tamaño renderizado de la imagen). El
    // evento 'load' no hace bubbling, así que se escucha en fase de captura sobre el wrapper.
    wrapper.addEventListener(
      'load',
      function (e) {
        if (e.target && e.target.tagName === 'IMG') scheduleResize();
      },
      true
    );
  }

  function applyForBreakpoint() {
    if (DESKTOP_QUERY.matches) {
      createLenis();
    } else {
      destroyLenis();
    }
  }

  applyForBreakpoint();
  DESKTOP_QUERY.addEventListener('change', applyForBreakpoint);

  window.addEventListener('pagehide', destroyLenis);
})();
